import React, { ChangeEvent, useState } from 'react'
import { capitalCase } from 'change-case'
import { format } from 'date-fns'
import { get, map } from 'lodash-es'
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Filter,
  Gift,
  ListFilter,
  LucideIcon,
  TrendingUp,
} from 'lucide-react'

import { useTenant } from '@rewardops/auth'
import { Skeleton, useLocale } from '@rewardops/ui'
import { lightenColor } from '@rewardops/utils'

import { useApi } from '~/components/hooks/use-api'
import { useEarningHistory } from '~/components/hooks/use-earning-history'
import { useFormat } from '~/components/hooks/use-format'
import { useOrdersQuery } from '~/components/maple/pages/order-history/filters'
import { useTranslation } from '~/app/i18n/client'
import { DashboardPagination } from '../dashboard-pagination'
import { PAWSectionType } from './paw-section-types'

enum ActivityTab {
  Earning = 'earning',
  Redemption = 'redemption',
}

type TabSelectorBtnProps = {
  Icon: LucideIcon
  title: string
  active: boolean
  onSelect: () => void
}

const TabSelectorBtn: React.FC<TabSelectorBtnProps> = ({
  Icon,
  title,
  active,
  onSelect,
}: TabSelectorBtnProps) => {
  const tenant = useTenant()
  const hoverColor = lightenColor(
    tenant?.customStyling?.primaryColor ?? '#FFFFFF',
    60,
  )
  return (
    <button
      className={`flex flex-1 items-center gap-2 rounded px-3 py-1.5 text-sm text-gray-500 ${
        active
          ? `bg-[var(--hover-color)] font-semibold text-tenant-primary-color`
          : ''
      }`}
      style={
        {
          '--hover-color': hoverColor,
        } as React.CSSProperties
      }
      onClick={onSelect}
    >
      <Icon size={16} />
      {title}
    </button>
  )
}

type TabSelectorProps = {
  tab: ActivityTab
  setTab: (tab: ActivityTab) => void
}

const TabSelector: React.FC<TabSelectorProps> = ({
  tab,
  setTab,
}: TabSelectorProps) => {
  const { t } = useTranslation()
  const onClick = (tabType: ActivityTab) => {
    setTab(tabType)
  }

  return (
    <div className="flex rounded bg-gray-100 px-2 py-1">
      <TabSelectorBtn
        Icon={TrendingUp}
        title={t('pawDashboards.earningHistory.earning')}
        active={tab === ActivityTab.Earning}
        onSelect={() => onClick(ActivityTab.Earning)}
      />
      <TabSelectorBtn
        Icon={Gift}
        title={t('pawDashboards.earningHistory.redemptions')}
        active={tab === ActivityTab.Redemption}
        onSelect={() => onClick(ActivityTab.Redemption)}
      />
    </div>
  )
}

type ActivityDropdownProps = {
  Icon: LucideIcon
  options: { value: string; label: string; selected: boolean }[]
  label: string
  onSelect: (e: ChangeEvent<HTMLSelectElement>) => void
}

const ActivityDropdown: React.FC<ActivityDropdownProps> = ({
  label,
  Icon,
  options,
  onSelect,
}: ActivityDropdownProps) => {
  const { t } = useTranslation()
  const defaultSelected = options.every((option) => option.selected === false)

  return (
    <div className="min-w-40 relative flex items-center">
      <div className="absolute left-3">
        <Icon size={16} />
      </div>
      <select
        className={`flex w-full items-center rounded border border-gray-300 
        bg-gray-100 px-3 py-2 text-center text-sm 
        focus-visible:outline-none focus-visible:ring-2
        focus-visible:ring-tenant-primary-color`}
        onChange={onSelect}
      >
        <option value="" className="" selected={defaultSelected}>
          {t(`pawDashboards.earningHistory.${label}`)}
        </option>
        {options?.map((option) => (
          <option
            key={option?.value}
            value={option?.value}
            selected={option?.selected}
          >
            {option?.label}
          </option>
        ))}
      </select>
    </div>
  )
}

type ActivityOptionsProps = {
  selectedValue: string
  options: Record<string, string>
  onSelect: (type: string) => void
  defaultOption?: string
  Icon: LucideIcon
}

const ActivityOptions: React.FC<ActivityOptionsProps> = ({
  selectedValue,
  options,
  onSelect,
  defaultOption = 'allActivity',
  Icon,
}: ActivityOptionsProps) => {
  const normalizedOptions: {
    value: string
    label: string
    selected: boolean
  }[] = []

  Object.keys(options).forEach((key) =>
    normalizedOptions.push({
      value: key,
      label: options[key] ?? '',
      selected: key === selectedValue,
    }),
  )

  const onOptionSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    onSelect(e.target.value)
  }

  if (!normalizedOptions?.length) {
    return null
  }

  return (
    <div className="flex gap-2">
      <ActivityDropdown
        label={defaultOption}
        options={normalizedOptions}
        Icon={Icon}
        onSelect={onOptionSelect}
      />
    </div>
  )
}

type OrdersProps = {
  orders: Record<string, any>[]
  pagination: {
    current: number
    pages: number
  }
  getPrevOrNext: (action: 'next' | 'prev') => void
  selectedDate: string
}

const Orders: React.FC<OrdersProps> = ({
  orders,
  pagination,
  getPrevOrNext,
  selectedDate,
}: OrdersProps) => {
  const { t: translate } = useTranslation()
  const tenant = useTenant()
  const { locale } = useLocale()
  const { formatNumber } = useFormat()

  if (!orders?.length) {
    return (
      <div className="flex flex-wrap items-center justify-center gap-1 p-6">
        <Gift size={18} className="text-tenant-primary-color" />
        {selectedDate
          ? translate('pawDashboards.earningHistory.noRedemptionsWithFilter')
          : translate('pawDashboards.earningHistory.noRedemptions')}
        {selectedDate ? (
          <div className="flex items-center gap-1">
            <Filter size={18} className="text-tenant-primary-color" />
            {translate('pawDashboards.earningHistory.adjustFilters')}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="mt-0 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
      {orders?.map((order, index) => {
        const lineItem = order?.suppliers?.[0]?.line_items?.[0]
        const memberPaid = order?.member_paid_total?.find(
          (total: { currency_code: string; amount: number }) =>
            total?.currency_code === tenant?.defaultPoints,
        )

        const itemImage = lineItem?.images?.[0]

        return (
          <div
            key={order?.program_order_code ?? order?.id}
            className="flex items-center gap-3 border-b px-4 py-3 last:border-b-0"
          >
            <div className="h-16 w-16 rounded-md border p-2">
              <img
                alt={itemImage?.name}
                src={itemImage?.url}
                className="h-full w-full object-contain"
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{lineItem?.name}</p>
              <p className="truncate text-xs text-muted-foreground">
                {capitalCase(lineItem?.type ?? '')}
                {' — '}
                {capitalCase(lineItem?.fulfillment?.status ?? '')}
              </p>
            </div>
            <div className="flex-shrink-0 text-right">
              {memberPaid && (
                <p className="text-sm font-semibold tabular-nums text-destructive">
                  -{formatNumber(memberPaid?.amount)} pts
                </p>
              )}

              <p className="text-xs tabular-nums text-muted-foreground">
                {new Date(order?.created_at)?.toLocaleDateString(locale, {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  timeZone: 'UTC',
                })}
              </p>
            </div>
          </div>
        )
      })}
      {orders?.length ? (
        <DashboardPagination
          totalNumberOfPages={pagination?.pages ?? 0}
          requestedPage={pagination?.current ?? 1}
          getPrevOrNext={getPrevOrNext}
        />
      ) : null}
    </div>
  )
}

type TransactionsProps = {
  transactions: Record<string, any>[]
  columns: {
    label: string
    value: string
    default?: boolean
    identifier: string
  }[]
  rawResponse: Record<string, any>
  getPrevOrNext: (action: 'next' | 'prev') => void
  content: Record<string, any>
  selectedOption: string
  selectedDate: string
}

const Transactions: React.FC<TransactionsProps> = ({
  transactions,
  columns,
  rawResponse,
  content,
  selectedOption,
  selectedDate,
  getPrevOrNext,
}: TransactionsProps) => {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())
  const { locale } = useLocale()
  const { t: translate } = useTranslation()
  const { formatNumber } = useFormat()

  const { shouldExpandRows = true, dateFieldName, currencies } = content

  const getTotal = (transactions: { amount: number; currency: string }[]) => {
    return transactions?.reduce((acc, transaction) => {
      if (currencies?.includes(transaction?.currency?.toLowerCase())) {
        acc = acc + (transaction?.amount ?? 0)
      }

      return acc
    }, 0)
  }

  const handleSectionOpenClose = (key: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  if (!transactions?.length) {
    const isFilterSelected = Boolean(selectedOption || selectedDate)
    return (
      <div className="flex items-center justify-center gap-1 p-6">
        <TrendingUp size={18} className="text-tenant-primary-color" />
        {isFilterSelected
          ? translate('pawDashboards.earningHistory.noEarningsWithFilter')
          : translate('pawDashboards.earningHistory.noEarnings')}
        {isFilterSelected ? (
          <div className="flex items-center gap-1">
            <Filter size={18} className="text-tenant-primary-color" />
            {translate('pawDashboards.earningHistory.adjustFilters')}
          </div>
        ) : null}
      </div>
    )
  }

  const defaultValueViews = {
    date: (transaction: any) => (
      <div className="flex items-center gap-1">
        {transaction?.followOnActions?.length > 1 && shouldExpandRows ? (
          openSections?.has(transaction?.id) ? (
            <ChevronDown size={16} className="text-muted-foreground" />
          ) : (
            <ChevronRight size={16} className="text-muted-foreground" />
          )
        ) : (
          <span className="flex w-4 shrink-0"></span>
        )}
        <div className="text-muted-foreground">
          {new Date(
            transaction?.[dateFieldName ?? 'interactionDate'],
          )?.toLocaleDateString(locale, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
          })}
        </div>
      </div>
    ),
    description: (transaction: any) => (
      <div className="truncate text-sm">{transaction?.description}</div>
    ),
    amount: (transaction: any) => (
      <div className="font-semibold text-green-700">
        +{formatNumber(getTotal(transaction?.followOnActions))}
      </div>
    ),
  }

  const getColumnValue = (
    transaction: any,
    column: { value: string; default?: boolean; identifier: string },
  ) => {
    if (column?.default) {
      const func = (defaultValueViews as any)?.[column?.identifier]

      if (func) {
        return func(transaction)
      }
    }

    if (column?.value?.includes('[]')) {
      const [first, rest] = column?.value.split('.[].')
      let value = map(get(transaction, first ?? ''), rest)

      if (Array.isArray(value)) {
        value = Array.from(new Set(value))?.filter(Boolean)
      }

      return <div className="truncate text-sm">{value?.join(' / ')}</div>
    }

    return (
      <div className="truncate text-sm">{get(transaction, column?.value)}</div>
    )
  }

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr)) auto`,
    gap: '0.75rem',
  }

  return (
    <div>
      <div
        className="items-center border-b border-border bg-muted/40 px-4 py-1.5"
        style={gridStyle}
      >
        {columns.map((column, index) => (
          <div
            key={column?.label}
            className={`text-left text-xs font-medium text-muted-foreground ${
              index === columns?.length - 1 ? 'justify-self-end' : ''
            }`}
          >
            {column?.label}
          </div>
        ))}
      </div>
      {transactions?.map((transaction) => (
        <div key={transaction?.id} className="flex flex-col text-sm">
          <button
            className={`items-center px-4 py-3 text-left ${
              shouldExpandRows ? 'cursor-pointer' : ''
            }`}
            style={gridStyle}
            onClick={() => handleSectionOpenClose(transaction?.id)}
            disabled={
              !shouldExpandRows || transaction?.followOnActions?.length <= 1
            }
          >
            {columns.map((col, index) => (
              <div
                key={col?.value}
                className={`${
                  index === columns?.length - 1 ? 'justify-self-end' : ''
                }`}
              >
                {getColumnValue(transaction, col) ?? '-'}
              </div>
            ))}
          </button>
          {transaction?.followOnActions?.length > 1 &&
          openSections?.has(transaction?.id) ? (
            <div className="flex flex-col space-y-1.5 px-4 pb-3 pl-11 text-sm">
              {transaction?.followOnActions?.map(
                (action: any, index: number) => (
                  <div
                    key={`${action?.id}_${index}`}
                    className="flex justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-tenant-primary-color capitalize"></div>
                      {`${capitalCase(
                        action?.currency ?? '',
                      )} - ${action?.currencySubType?.toLowerCase()} ${action?.transactionType?.toLowerCase()}`}
                    </div>
                    <div className="font-semibold">
                      +{formatNumber(action?.amount)}
                    </div>
                  </div>
                ),
              )}
            </div>
          ) : null}
          <hr />
        </div>
      ))}
      {transactions?.length ? (
        <DashboardPagination
          totalNumberOfPages={rawResponse?.totalNumberOfPages ?? 0}
          requestedPage={rawResponse?.requestedPage ?? 1}
          getPrevOrNext={getPrevOrNext}
        />
      ) : null}
    </div>
  )
}

export const EarningHistory: React.FC<PAWSectionType> = ({
  content,
  data,
}: PAWSectionType) => {
  const { api } = useApi()
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [page, setPage] = useState<number>(1)
  const { t } = useTranslation()

  const defaultDateParams = {
    start_date: '',
    end_date: '',
    fromDate: '',
    toDate: '',
    selectedOption: '',
  }

  const [selectedInteractionType, setSelectedInteractionType] =
    useState<string>('')
  const [dateParams, setDateParams] = useState(defaultDateParams)

  const [tab, setTab] = useState<ActivityTab>(ActivityTab.Earning)

  const { queryParams } = useOrdersQuery()

  const { locale } = useLocale()

  const { data: transactions, isLoading: transactionsLoading } =
    api.pawDashboards.getMemberInteractionTransactions.useQuery({
      pageNumber,
      interactionType: selectedInteractionType
        ? selectedInteractionType
        : content?.interactionTypes
        ? content?.interactionTypes?.join(',')
        : 'value',
      fetchPayloads: true,
      ...(dateParams?.toDate ? { toDate: dateParams?.toDate } : {}),
      ...(dateParams?.fromDate ? { fromDate: dateParams?.fromDate } : {}),
    })

  // for the activity widget we are only showing giftcards and merchandise
  const { data: orders, isLoading: ordersLoading } = api.orders.getAll.useQuery(
    {
      ...queryParams,
      per_page_count: 10,
      page,
      sort_by: 'newest',
      item_type: 'giftcard,merchandise',
      accept_language: locale,
      ...(dateParams?.start_date ? { start_date: dateParams?.start_date } : {}),
      ...(dateParams?.end_date ? { end_date: dateParams?.end_date } : {}),
    },
  )

  const getPrevOrNext = (action: 'prev' | 'next') => {
    const setterFn = tab === ActivityTab.Redemption ? setPage : setPageNumber

    action === 'next'
      ? setterFn((prev) => prev + 1)
      : setterFn((prev) => prev - 1)
  }

  const onSelectInteractionType = (type: string) => {
    setSelectedInteractionType(type)
  }

  const onSelectDate = (selectedDate: string) => {
    const currentDate = new Date()
    let startDate = new Date()
    const currentYear = new Date().getFullYear()

    if (selectedDate == '1') {
      startDate = new Date(currentYear, 0, 1)
    } else {
      startDate.setDate(currentDate.getDate() - Number(selectedDate))
    }

    if (tab === ActivityTab.Redemption) {
      setDateParams((prev) => ({
        ...prev,
        start_date: startDate.toISOString(),
        end_date: currentDate.toISOString(),
        selectedOption: selectedDate,
      }))
    } else {
      setDateParams((prev) => ({
        ...prev,
        fromDate: format(startDate, 'yyyyMMdd'),
        toDate: format(currentDate, 'yyyyMMdd'),
        selectedOption: selectedDate,
      }))
    }
  }

  const onTabChange = (tabType: ActivityTab) => {
    setTab(tabType)
    // on tab change reset the date logic
    setDateParams((prev) => ({
      ...prev,
      ...defaultDateParams,
    }))
  }

  const {
    customColumns,
    sectionId,
    title,
    interactionTypeMap,
    metadata,
    ...rest
  } = content

  const { columns, transactions: normalizedTransactions } = useEarningHistory({
    transactions,
    content,
  })

  const isLoading = transactionsLoading || ordersLoading

  return (
    <section id={sectionId} data-testid={sectionId}>
      {title ? (
        <h3 className="mb-4 text-xl font-semibold">
          {title ?? 'Earning History'}
        </h3>
      ) : null}
      {isLoading ? (
        <Skeleton className="h-[300px] w-full" />
      ) : (
        <div className="rounded-md border bg-white">
          <div className="flex flex-col gap-3 p-3 md:flex-row md:justify-between md:gap-0">
            <TabSelector tab={tab} setTab={onTabChange} />
            <div className="flex gap-3">
              {tab === ActivityTab.Earning && (
                <ActivityOptions
                  selectedValue={selectedInteractionType}
                  options={interactionTypeMap ?? {}}
                  onSelect={onSelectInteractionType}
                  defaultOption="allActivity"
                  Icon={ListFilter}
                />
              )}
              <ActivityOptions
                selectedValue={dateParams?.selectedOption}
                options={{
                  30: 'Last 30 Days',
                  90: 'Last 90 Days',
                  1: 'This year',
                }}
                onSelect={onSelectDate}
                defaultOption="allTime"
                Icon={Calendar}
              />
            </div>
          </div>
          <hr />
          {tab === ActivityTab.Redemption ? (
            <Orders
              orders={orders?.result?.orders}
              pagination={orders?.pagination as any}
              getPrevOrNext={getPrevOrNext}
              selectedDate={dateParams?.selectedOption}
            />
          ) : (
            <Transactions
              transactions={normalizedTransactions?.interactionResponses ?? []}
              columns={columns}
              content={{
                ...rest,
                currencies:
                  rest?.currencies?.map((currency: string) =>
                    currency?.toLowerCase(),
                  ) ?? [],
              }}
              rawResponse={normalizedTransactions}
              getPrevOrNext={getPrevOrNext}
              selectedOption={selectedInteractionType}
              selectedDate={dateParams?.selectedOption}
            />
          )}
        </div>
      )}
    </section>
  )
}
